"use client";

import React, { useEffect, useRef, useCallback, Children } from "react";
import styled from "styled-components";

import Flex, { FlexProps } from "../Flex";
import Box, { BoxProps } from "../Box";
import Icon from "../Icon";

import useBoolean from "../../hooks/useBoolean";
import useEventListener from "../../hooks/useEventListener";

import { getFont } from "../utils";

type Props = {
  center?: boolean;
  separator?: boolean;
  size?: "small" | "regular";
  styleOverrides?: BoxProps & FlexProps;
  children: React.ReactNode;
};

type BarWrapperProps = Pick<Props, "size"> & { innerOverflows?: boolean };
const BarWrapper = styled(Flex).attrs<BarWrapperProps>(props => {
  return {
    width: "100%",
    height: props.size === "small" ? "28px" : "36px",
    px: props.innerOverflows ? "40px" : "0",
  };
})<BarWrapperProps>`
  position: relative;
  overflow: hidden;
  scroll-behavior: smooth;
  button {
    ${props => getFont(props.size === "small" ? "ui.small" : "ui.regular", null, props)};
  }
  a {
    button {
      height: 100%;
    }
  }
`;

type OverflowOverlayProps = Pick<Props, "size"> & { side?: "left" | "right" };
const OverflowOverlay = styled(Flex).attrs<OverflowOverlayProps>(props => {
  return {
    width: "49px",
    height: props.size === "small" ? "28px" : "36px",
  };
})<OverflowOverlayProps>`
  z-index: 100;
  background: ${props =>
    props.side === "right"
      ? "linear-gradient(270deg, #FFFFFF 45.48%, rgba(255, 255, 255, 0) 100%)"
      : "linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, #ffffff 40.1%)"};
  flex-flow: ${props => (props.side === "right" ? "row-reverse" : "row")};
`;

const ScrollButton = styled.button`
  display: flex;
  align-items: center;
`;

const TabBar: React.FC<Props> = ({
  center = true,
  size = "regular",
  separator = false,
  children,
  styleOverrides,
}) => {
  const outerRef = useRef<HTMLDivElement>();
  const innerRef = useRef<HTMLDivElement>();

  const [innerOverflows, setTrue, setFalse] = useBoolean(false);

  const handleResize = useCallback(() => {
    if (outerRef.current.clientWidth < innerRef.current.clientWidth && !innerOverflows) {
      setTrue();
    } else if (outerRef.current.clientWidth > innerRef.current.clientWidth && innerOverflows) {
      setFalse();
    }
  }, [innerOverflows, setTrue, setFalse]);

  useEffect(() => {
    handleResize();
  }, [handleResize]);

  useEventListener("resize", handleResize);

  const scroll = direction => {
    const numberOfItems = Children.count(children);
    if (direction === "left") {
      outerRef.current.scrollLeft -= innerRef.current.clientWidth / numberOfItems;
    } else if (direction === "right") {
      outerRef.current.scrollLeft += innerRef.current.clientWidth / numberOfItems;
    }
  };

  function renderOverlay(direction) {
    return (
      <OverflowOverlay size={size} side={direction}>
        <ScrollButton onClick={() => scroll(direction)}>
          <Icon iconId={`navigation.chevron-${direction}`} size="24" />
        </ScrollButton>
      </OverflowOverlay>
    );
  }
  return (
    <Box>
      {innerOverflows && (
        <Flex width="100%" justifyContent="space-between" position="absolute">
          {renderOverlay("left")}
          {renderOverlay("right")}
        </Flex>
      )}
      {separator && (
        <Box position="absolute" bottom="0" left="0" right="0" height="1" background="border" />
      )}
      <BarWrapper ref={outerRef} size={size} innerOverflows={innerOverflows} {...styleOverrides}>
        <Box ref={innerRef} mx={center ? "auto" : null}>
          {children}
        </Box>
      </BarWrapper>
    </Box>
  );
};

export default TabBar;
